import React, { useEffect, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { useGymFuel, BOOST_MS } from '../state/gymFuel';

// EnergyBoost — the minute a GangstaFuel gives you, on screen (2026-10-31).
//
// Khari: "When the user drinks it, their movement is sped up for 1 minute and
// the screen is brighter." The speed is the walk code's (state/gymFuel
// boostFactor, read in InteriorWalk and walkBody); this is the rest of it:
//
//   · THE SCREEN BRIGHTER (BoostGlow, mounted INSIDE the scene so it holds the
//     world's own renderer element, never a guess at which canvas is which): a
//     CSS filter on that canvas only — never the HUD — eased in and out so it
//     reads as a rush rather than a glitch;
//   · A QUIET CHIP with the time left, in the brand's lime, so the speed has a
//     reason you can see and an end you can see coming.
//
// It follows you out of the gym: the boost is yours, not the room's.

const LIME = '#74f84b';
const FILTER = 'brightness(1.22) saturate(1.12) contrast(1.03)';
const EASE_MS = 700;

const chip = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  top: 'calc(max(16px, env(safe-area-inset-top)) + 46px)', zIndex: 60,
  padding: '6px 14px', borderRadius: 999, pointerEvents: 'none',
  background: 'rgba(10,12,8,0.82)', border: `1px solid ${LIME}`, color: LIME,
  font: '800 12.5px system-ui', letterSpacing: 0.6, fontVariantNumeric: 'tabular-nums',
  boxShadow: `0 0 18px rgba(116,248,75,0.35)`, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
};

/** In the scene: the world's canvas brightens while the boost runs. */
export function BoostGlow() {
  const el = useThree((s) => s.gl.domElement);
  const until = useGymFuel((s) => s.boostUntil);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const left = until - Date.now();
    setOn(left > 0);
    if (left <= 0) return undefined;
    const t = setTimeout(() => setOn(false), left);
    return () => clearTimeout(t);
  }, [until]);
  useEffect(() => {
    if (!el) return undefined;
    el.style.transition = `filter ${EASE_MS}ms ease`;
    el.style.filter = on ? FILTER : '';
    return undefined;
  }, [el, on]);
  // Never leave the world brightened behind an unmount.
  useEffect(() => () => { if (el) el.style.filter = ''; }, [el]);
  return null;
}

/** On the HUD: the time left. */
export default function EnergyBoost() {
  const until = useGymFuel((s) => s.boostUntil);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    const tick = () => setLeft(Math.max(0, until - Date.now()));
    tick();
    if (until <= Date.now()) return undefined;
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
  }, [until]);

  if (left <= 0) return null;
  const s = Math.ceil(left / 1000);
  const fading = left < 5000;
  return (
    <div style={{ ...chip, opacity: fading ? 0.55 + 0.45 * Math.abs(Math.sin(left / 160)) : 1 }} role="status" aria-label={`GangstaFuel: ${s} seconds left`}>
      ⚡ FUELED · {Math.floor(s / 60)}:{String(s % 60).padStart(2, '0')}
      <span style={{ display: 'block', height: 2, marginTop: 4, borderRadius: 2, background: LIME, width: `${(left / BOOST_MS) * 100}%`, opacity: 0.8 }} />
    </div>
  );
}
